// ════════════════════════════════════════════════════════════════════════════════
// ModuloERI.js — Estado de Resultados Integral (Ola 3 — Frontend)
// ─────────────────────────────────────────────────────────────────────────────
// Vista profesional contable con 4 KPIs principales arriba, el P&G tradicional
// abajo y desglose por línea de servicio. Filtros: rango fechas (presets +
// personalizado) + selector de empresa (consolidado o individual).
// ════════════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { exportarExcel } from './exportExcel';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fmtCop = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;
const fmtFecha = (raw) => {
  if (!raw) return '—';
  try { return new Date(raw).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' }); }
  catch { return '—'; }
};

// Helpers de fecha
const hoy = () => new Date().toISOString().slice(0, 10);
const inicioMes = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const finMes = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
};
const inicioAnio = () => `${new Date().getFullYear()}-01-01`;
const finAnio = () => `${new Date().getFullYear()}-12-31`;

const ModuloERI = ({ user }) => {
  const [desde, setDesde] = useState(inicioMes());
  const [hasta, setHasta] = useState(finMes());
  const [empresaId, setEmpresaId] = useState('');
  const [empresas, setEmpresas] = useState([]);
  const [eri, setEri] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vista, setVista] = useState('resumen'); // resumen | lineas | detalle
  const [presetActivo, setPresetActivo] = useState('mes_actual');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ── Cargar empresas ──────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/companies`, { headers })
      .then(r => setEmpresas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEmpresas([]));
    // eslint-disable-next-line
  }, []);

  // ── Cargar ERI ───────────────────────────────────────────────────────────
  const cargarERI = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ desde, hasta });
      if (empresaId) params.append('empresaId', empresaId);
      params.append('vista', 'completa');
      const r = await axios.get(`${API}/eri?${params.toString()}`, { headers });
      setEri(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar ERI');
    }
    setLoading(false);
    // eslint-disable-next-line
  }, [desde, hasta, empresaId]);

  useEffect(() => { cargarERI(); }, [cargarERI]);

  // ── Presets de fechas ────────────────────────────────────────────────────
  const aplicarPreset = (p) => {
    setPresetActivo(p);
    switch (p) {
      case 'mes_actual':    setDesde(inicioMes()); setHasta(hoy()); break;
      case 'mes_anterior':  setDesde(inicioMes(-1)); setHasta(finMes(-1)); break;
      case 'anio_actual':   setDesde(inicioAnio()); setHasta(hoy()); break;
      case 'anio_completo': setDesde(inicioAnio()); setHasta(finAnio()); break;
      case 'ultimos_7':     {
        const d = new Date(); d.setDate(d.getDate() - 6);
        setDesde(d.toISOString().slice(0, 10)); setHasta(hoy()); break;
      }
      case 'ultimos_30':    {
        const d = new Date(); d.setDate(d.getDate() - 29);
        setDesde(d.toISOString().slice(0, 10)); setHasta(hoy()); break;
      }
      case 'trimestre':     {
        const d = new Date(); d.setMonth(d.getMonth() - 2); d.setDate(1);
        setDesde(d.toISOString().slice(0, 10)); setHasta(hoy()); break;
      }
      default: break;
    }
  };

  // ── Exportar a Excel ─────────────────────────────────────────────────────
  const exportarERI = () => {
    if (!eri) return;
    const filas = [];
    filas.push({ s: 'PERIODO', d: 'Desde', v: eri.meta.desde });
    filas.push({ s: 'PERIODO', d: 'Hasta', v: eri.meta.hasta });
    filas.push({ s: 'PERIODO', d: 'Empresa', v: eri.meta.empresaNombre });
    filas.push({ s: '', d: '', v: '' });

    filas.push({ s: 'INGRESOS', d: 'Ingresos por servicios', v: eri.ingresos.servicios });
    filas.push({ s: 'INGRESOS', d: 'Ingresos por productos', v: eri.ingresos.productos });
    filas.push({ s: 'INGRESOS', d: '─── TOTAL INGRESOS', v: eri.ingresos.total });
    filas.push({ s: '', d: '', v: '' });

    filas.push({ s: 'COSTOS', d: 'Costo de servicios (insumos)', v: eri.costoVentas.servicios });
    filas.push({ s: 'COSTOS', d: 'Costo de venta de productos', v: eri.costoVentas.productos });
    filas.push({ s: 'COSTOS', d: '─── TOTAL COSTOS', v: eri.costoVentas.total });
    filas.push({ s: '', d: '', v: '' });

    filas.push({ s: 'UTILIDAD', d: 'Utilidad bruta servicios', v: eri.utilidadBruta.servicios });
    filas.push({ s: 'UTILIDAD', d: 'Utilidad bruta productos', v: eri.utilidadBruta.productos });
    filas.push({ s: 'UTILIDAD', d: '─── UTILIDAD BRUTA TOTAL', v: eri.utilidadBruta.total });
    filas.push({ s: 'UTILIDAD', d: 'Margen bruto %', v: eri.utilidadBruta.margen.toFixed(2) });
    filas.push({ s: '', d: '', v: '' });

    eri.porLinea.forEach(l => {
      filas.push({ s: `LÍNEA: ${l.nombre}`, d: 'Ingreso', v: l.ingresoServicio });
      filas.push({ s: `LÍNEA: ${l.nombre}`, d: 'Costo total', v: l.costoServicio });
      // ✅ FIX ERI-LINEA-002: el desglose también va al Excel, si no el anexo
      // no sirve para auditar de dónde sale el costo de cada línea.
      if (l.costoInsumos > 0)   filas.push({ s: `LÍNEA: ${l.nombre}`, d: '  · Insumos de taller', v: l.costoInsumos });
      if (l.costoProductos > 0) filas.push({ s: `LÍNEA: ${l.nombre}`, d: '  · Costo de productos vendidos', v: l.costoProductos });
      filas.push({ s: `LÍNEA: ${l.nombre}`, d: 'Utilidad bruta', v: l.utilidadBruta });
      filas.push({ s: `LÍNEA: ${l.nombre}`, d: 'Margen %', v: l.margenPct.toFixed(2) });
      filas.push({ s: '', d: '', v: '' });
    });

    filas.push({ s: 'GASTOS', d: 'Personal (nómina)', v: eri.gastos.personal });
    filas.push({ s: 'GASTOS', d: 'Operativos', v: eri.gastos.operativos });
    filas.push({ s: 'GASTOS', d: 'Fijos', v: eri.gastos.fijos });
    filas.push({ s: 'GASTOS', d: 'Administrativos', v: eri.gastos.administrativos });
    filas.push({ s: 'GASTOS', d: 'Financieros', v: eri.gastos.financieros });
    filas.push({ s: 'GASTOS', d: 'Fiscales', v: eri.gastos.fiscales });
    filas.push({ s: 'GASTOS', d: '─── TOTAL GASTOS', v: eri.gastos.total });
    filas.push({ s: '', d: '', v: '' });

    filas.push({ s: 'RESULTADO', d: 'Utilidad Operativa', v: eri.utilidadOperativa.valor });
    filas.push({ s: 'RESULTADO', d: 'Margen Operativo %', v: eri.utilidadOperativa.margen.toFixed(2) });
    filas.push({ s: 'RESULTADO', d: 'Utilidad antes de impuestos', v: eri.utilidadAntesImpuestos.valor });
    filas.push({ s: 'RESULTADO', d: '═══ UTILIDAD NETA', v: eri.utilidadNeta.valor });
    filas.push({ s: 'RESULTADO', d: 'Margen Neto %', v: eri.utilidadNeta.margen.toFixed(2) });

    // ✅ ERI-EXPORT-001: incluir anexos y cartera en el export (antes salía muy
    // básico, sin sustancia). Ahora es un informe completo digno de contadora.
    const inf = eri.informe;
    if (inf) {
      filas.push({ s: '', d: '', v: '' });
      filas.push({ s: 'ANEXO 1 · VENTAS', d: '── Órdenes del período (causación) ──', v: '' });
      (inf.anexos?.ventas || []).forEach(v => filas.push({ s: 'ANEXO VENTAS', d: `${v.numeroOrden} · ${v.fecha} · ${v.clienteNombre}`, v: v.total }));

      filas.push({ s: '', d: '', v: '' });
      filas.push({ s: 'ANEXO 2 · COSTOS', d: '── Costo de ventas por categoría ──', v: '' });
      (inf.anexos?.costos || []).forEach(c => filas.push({ s: 'ANEXO COSTOS', d: `${c.categoria} (ingreso ${c.ingreso})`, v: c.costo }));

      filas.push({ s: '', d: '', v: '' });
      filas.push({ s: 'ANEXO 3 · GASTOS', d: '── Gastos del período ──', v: '' });
      (inf.anexos?.gastos || []).forEach(g => filas.push({ s: 'ANEXO GASTOS', d: g.categoria, v: g.monto }));

      if ((inf.anexos?.compras || []).length) {
        filas.push({ s: '', d: '', v: '' });
        filas.push({ s: 'ANEXO 4 · COMPRAS', d: '── Compras de mercancía ──', v: '' });
        inf.anexos.compras.forEach(c => filas.push({ s: 'ANEXO COMPRAS', d: `${c.fecha} · ${c.proveedor} · ${c.concepto}`, v: c.monto }));
      }

      // ✅ ERI-ANEXO-EGRESOS-001: el detalle de egresos SÍ venía del backend,
      // pero la exportación se lo saltaba. Sin él, el Excel mostraba los gastos
      // agrupados por categoría y no había forma de auditar comprobante por
      // comprobante ni de encontrar un egreso puntual mal clasificado.
      if ((inf.anexos?.egresos || []).length) {
        filas.push({ s: '', d: '', v: '' });
        filas.push({ s: 'ANEXO 5 · EGRESOS', d: '── Detalle de egresos del período ──', v: '' });
        inf.anexos.egresos.forEach(e => filas.push({
          s: 'ANEXO EGRESOS',
          d: `${e.numero} · ${e.fecha} · ${e.categoria} · ${e.concepto}`,
          v: e.monto
        }));
        filas.push({
          s: 'ANEXO EGRESOS',
          d: '── TOTAL EGRESOS DEL PERÍODO ──',
          v: inf.anexos.egresos.reduce((a, e) => a + (Number(e.monto) || 0), 0)
        });
      }

      if (inf.cartera) {
        filas.push({ s: '', d: '', v: '' });
        filas.push({ s: 'CARTERA CxC', d: '── Por cobrar (informativo) ──', v: inf.cartera.cxc.total });
        (inf.cartera.cxc.detalle || []).forEach(c => filas.push({ s: 'CxC', d: `${c.numeroOrden} · ${c.fecha} · ${c.clienteNombre}`, v: c.saldo }));
        filas.push({ s: '', d: '', v: '' });
        filas.push({ s: 'CARTERA CxP', d: '── Por pagar (informativo) ──', v: inf.cartera.cxp.total });
        (inf.cartera.cxp.detalle || []).forEach(c => filas.push({ s: 'CxP', d: `${c.fecha} · ${c.proveedor} · ${c.concepto}`, v: c.saldo }));
      }

      if (inf.inventario) {
        filas.push({ s: '', d: '', v: '' });
        filas.push({ s: 'INVENTARIO', d: 'Compras del período', v: inf.inventario.comprasDelPeriodo });
        filas.push({ s: 'INVENTARIO', d: 'Stock al costo', v: inf.inventario.alCosto });
        filas.push({ s: 'INVENTARIO', d: 'Stock valorizado', v: inf.inventario.valorizado });
        filas.push({ s: 'INVENTARIO', d: 'Utilidad potencial en stock', v: inf.inventario.potencial });
      }
    }

    exportarExcel(filas, [
      { label: 'Sección', key: 's' },
      { label: 'Concepto', key: 'd' },
      { label: 'Valor', key: 'v' }
    ], `ERI_${eri.meta.desde}_a_${eri.meta.hasta}`);
  };

  // ── Imprimir ─────────────────────────────────────────────────────────────
  const imprimirERI = () => {
    if (!eri) return;
    const html = construirHtmlImprimir(eri);
    const w = window.open('', '_blank');
    if (!w) { alert('Permite ventanas emergentes para imprimir.'); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={s.wrapper}>
      {/* HEADER */}
      <div style={s.pageHeader}>
        <div>
          <h2 style={s.pageTitle}>📊 ERI — Estado de Resultados Integral</h2>
          <p style={s.pageSubtitle}>P&G en tiempo real · {eri?.meta?.empresaNombre || 'Cargando...'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={imprimirERI} disabled={!eri} style={{ ...s.btn, background: '#0284c7', color: '#fff' }}>🖨 Imprimir</button>
          <button onClick={exportarERI} disabled={!eri} style={{ ...s.btn, background: '#16a34a', color: '#fff' }}>📊 Exportar Excel</button>
        </div>
      </div>

      {/* FILTROS */}
      <div style={s.filtros}>
        <div style={s.presets}>
          {[
            { v: 'mes_actual',    l: 'Mes actual' },
            { v: 'mes_anterior',  l: 'Mes anterior' },
            { v: 'trimestre',     l: 'Último trimestre' },
            { v: 'anio_actual',   l: 'Año (YTD)' },
            { v: 'anio_completo', l: 'Año completo' },
            { v: 'ultimos_7',     l: 'Últimos 7 días' },
            { v: 'ultimos_30',    l: 'Últimos 30 días' },
          ].map(p => (
            <button key={p.v} onClick={() => aplicarPreset(p.v)}
              style={{ ...s.preset, ...(presetActivo === p.v ? s.presetActivo : {}) }}>
              {p.l}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={s.campoF}>
            <label style={s.labelF}>Desde</label>
            <input type="date" value={desde} onChange={e => { setDesde(e.target.value); setPresetActivo(''); }} style={s.inputF} />
          </div>
          <div style={s.campoF}>
            <label style={s.labelF}>Hasta</label>
            <input type="date" value={hasta} onChange={e => { setHasta(e.target.value); setPresetActivo(''); }} style={s.inputF} />
          </div>
          <div style={s.campoF}>
            <label style={s.labelF}>Empresa</label>
            <select value={empresaId} onChange={e => setEmpresaId(e.target.value)} style={{ ...s.inputF, minWidth: 220 }}>
              <option value="">Consolidado (todas)</option>
              {empresas.map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* MENSAJES */}
      {loading && <div style={s.cargando}>Calculando ERI...</div>}
      {error && <div style={s.error}>⚠ {error}</div>}

      {/* CONTENIDO */}
      {eri && !loading && (
        <>
          {/* KPIs principales */}
          <div style={s.kpisGrid}>
            <KPI icon="💵" label="Ingresos totales" value={fmtCop(eri.ingresos.total)} sub={`${eri.meta.cantidadOrdenes} órdenes`} color="#0284c7" />
            <KPI icon="💎" label="Utilidad bruta" value={fmtCop(eri.utilidadBruta.total)} sub={fmtPct(eri.utilidadBruta.margen)} color="#16a34a" />
            <KPI icon="⚙️" label="Utilidad operativa" value={fmtCop(eri.utilidadOperativa.valor)} sub={fmtPct(eri.utilidadOperativa.margen)} color={eri.utilidadOperativa.valor >= 0 ? '#7c3aed' : '#dc2626'} />
            <KPI icon="🏆" label="Utilidad NETA" value={fmtCop(eri.utilidadNeta.valor)} sub={fmtPct(eri.utilidadNeta.margen)} color={eri.utilidadNeta.valor >= 0 ? '#16a34a' : '#dc2626'} grande />
          </div>

          {/* TABS de vista */}
          <div style={s.tabs}>
            {[
              { v: 'informe', l: '📊 Estado de Resultados' },
              { v: 'lineas',  l: '🎯 Por línea de servicio' },
              { v: 'anexos',  l: '📎 Anexos y soporte' },
            ].map(t => (
              <button key={t.v} onClick={() => setVista(t.v)}
                style={{ ...s.tab, ...(vista === t.v ? s.tabActivo : {}) }}>
                {t.l}
              </button>
            ))}
          </div>

          {/* ✅ ERI-UNIFICADO-001: un solo Estado de Resultados normativo (antes
              P&G y "ERI Resumen" mostraban lo mismo). "Detalle" se absorbió en
              Anexos. Menos redundancia, más claridad contable. */}
          {vista === 'informe' && <VistaInforme eri={eri} />}
          {vista === 'lineas' && <VistaLineas eri={eri} />}
          {vista === 'anexos' && <VistaAnexos eri={eri} />}
        </>
      )}
    </div>
  );
};

// ─── Sub-componentes ─────────────────────────────────────────────────────────

const KPI = ({ icon, label, value, sub, color, grande }) => (
  <div style={{
    ...s.kpi,
    border: `2px solid ${color}`,
    background: grande ? `linear-gradient(135deg, ${color}15 0%, #fff 100%)` : '#fff'
  }}>
    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: grande ? 24 : 20, fontWeight: 800, color, marginTop: 8 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{sub}</div>}
  </div>
);

// ✅ ERI-COSTO-001: INFORME P&G — ingresos y costos por categoría de producto,
// gastos por categoría de egreso, regla $0 no se muestra, e inventario aparte.
const VistaInforme = ({ eri }) => {
  const inf = eri.informe;
  if (!inf) return <div style={s.card}>El informe no está disponible.</div>;

  // ✅ FIX ERI-CUADRE-001: si las cifras del cuerpo y las tarjetas volvieran a
  // divergir, el informe lo dice acá arriba en vez de mostrar dos números
  // distintos y dejar que el usuario lo descubra por su cuenta.
  const Alerta = () => {
    const c = inf.cuadre;
    if (!c || c.ok) return null;
    return (
      <div style={{
        background: '#fef2f2', border: '2px solid #fecaca', borderRadius: 12,
        padding: '14px 18px', marginBottom: 16
      }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: '#991b1b', marginBottom: 5 }}>
          ⚠️ Las cifras de este informe no cuadran
        </div>
        <div style={{ fontSize: 12.5, color: '#b91c1c', lineHeight: 1.6 }}>
          {c.mensaje}
          <br />
          Utilidad por la cadena: <strong>{fmtCop(c.utilidadPorCadena)}</strong> ·
          reportada: <strong>{fmtCop(c.utilidadReportada)}</strong> ·
          diferencia: <strong>{fmtCop(c.diferencia)}</strong>
        </div>
      </div>
    );
  };

  const Linea = ({ nombre, valor, indent, bold, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', paddingLeft: indent ? 20 : 0, fontWeight: bold ? 800 : 500, fontSize: bold ? 15 : 13, color: color || '#334155', borderTop: bold ? '1px solid #e2e8f0' : 'none' }}>
      <span>{nombre}</span><span>{fmtCop(valor)}</span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Alerta />
      <div style={s.card}>
        {/* INGRESOS por categoría */}
        <div style={{ fontSize: 12, fontWeight: 800, color: '#0284c7', letterSpacing: 0.5, marginBottom: 4 }}>INGRESOS</div>
        {inf.ingresos.porCategoria.map((c, i) => <Linea key={i} nombre={c.nombre} valor={c.valor} indent />)}
        <Linea nombre="Total ingresos" valor={inf.ingresos.total} bold color="#0284c7" />

        {/* COSTO DE VENTAS por categoría */}
        {inf.costoVentas.porCategoria.length > 0 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#dc2626', letterSpacing: 0.5, margin: '14px 0 4px' }}>(−) COSTO DE VENTAS</div>
            {inf.costoVentas.porCategoria.map((c, i) => <Linea key={i} nombre={c.nombre} valor={c.valor} indent />)}
            <Linea nombre="Total costo de ventas" valor={inf.costoVentas.total} bold color="#dc2626" />
          </>
        )}

        {/* UTILIDAD BRUTA */}
        <div style={{ margin: '10px 0', padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 16, color: '#16a34a' }}>
          <span>= UTILIDAD BRUTA</span><span>{fmtCop(inf.utilidadBruta)}</span>
        </div>

        {/* GASTOS por categoría */}
        {inf.gastos.porCategoria.length > 0 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', letterSpacing: 0.5, margin: '14px 0 4px' }}>(−) GASTOS OPERATIVOS</div>
            {inf.gastos.porCategoria.map((c, i) => <Linea key={i} nombre={c.nombre} valor={c.valor} indent />)}
            <Linea nombre="Total gastos" valor={inf.gastos.total} bold color="#b45309" />
          </>
        )}

        {/* UTILIDAD NETA */}
        <div style={{ marginTop: 12, padding: '14px 16px', background: inf.utilidadNeta >= 0 ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#dc2626,#b91c1c)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 20, color: '#fff' }}>
          <span>= UTILIDAD NETA</span><span>{fmtCop(inf.utilidadNeta)}</span>
        </div>
        {/* ✅ ERI-CAUSACION-001: sello normativo */}
        <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8', textAlign: 'center', fontStyle: 'italic' }}>
          Elaborado bajo principio de causación (devengo) · Los ingresos se reconocen al prestar el servicio, no al cobrar
        </div>
      </div>

      {/* INVENTARIO (informativo, NO afecta utilidad) */}
      <div style={{ ...s.card, border: '1px dashed #cbd5e1', background: '#f8fafc' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', letterSpacing: 0.5, marginBottom: 8 }}>
          📦 INVENTARIO <span style={{ fontWeight: 500, textTransform: 'none' }}>(informativo — no afecta la utilidad)</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <div style={{ padding: '12px 14px', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, color: '#64748b' }}>Compras del período</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#7c3aed' }}>{fmtCop(inf.inventario.comprasDelPeriodo)}</div>
          </div>
          <div style={{ padding: '12px 14px', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, color: '#64748b' }}>Stock al costo</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#334155' }}>{fmtCop(inf.inventario.alCosto)}</div>
          </div>
          <div style={{ padding: '12px 14px', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, color: '#64748b' }}>Stock valorizado (venta)</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#16a34a' }}>{fmtCop(inf.inventario.valorizado)}</div>
          </div>
          <div style={{ padding: '12px 14px', background: '#ecfdf5', borderRadius: 10, border: '1px solid #a7f3d0' }}>
            <div style={{ fontSize: 11, color: '#059669' }}>Utilidad potencial en stock</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#059669' }}>{fmtCop(inf.inventario.potencial || 0)}</div>
          </div>
        </div>
      </div>

      {/* ✅ ERI-CARTERA-001: CARTERA (informativo) — CxC por cobrar + CxP por pagar */}
      {inf.cartera && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <div style={{ ...s.card, borderTop: '3px solid #0284c7' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#0284c7', marginBottom: 4 }}>💰 CARTERA POR COBRAR (CxC)</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Ventas a crédito del período pendientes de cobro</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: '#0284c7' }}>{fmtCop(inf.cartera.cxc.total)}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{inf.cartera.cxc.detalle.length} {inf.cartera.cxc.detalle.length === 1 ? 'orden' : 'órdenes'} por cobrar</div>
          </div>
          <div style={{ ...s.card, borderTop: '3px solid #dc2626' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#dc2626', marginBottom: 4 }}>📋 CARTERA POR PAGAR (CxP)</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>Cuentas pendientes con proveedores</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: '#dc2626' }}>{fmtCop(inf.cartera.cxp.total)}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{inf.cartera.cxp.detalle.length} {inf.cartera.cxp.detalle.length === 1 ? 'cuenta' : 'cuentas'} por pagar</div>
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ ERI-COSTO-001: ANEXOS — soporte del informe (de dónde sale cada cifra)
const VistaAnexos = ({ eri }) => {
  const ax = eri.informe?.anexos;
  if (!ax) return <div style={s.card}>Anexos no disponibles.</div>;
  const Tabla = ({ titulo, headers, filas, color }) => (
    <div style={s.card}>
      <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 800, color: color || '#334155' }}>{titulo}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#f8fafc', color: '#64748b' }}>
          {headers.map((h, i) => <th key={i} style={{ textAlign: h.right ? 'right' : 'left', padding: '6px 10px', fontWeight: 600 }}>{h.label}</th>)}
        </tr></thead>
        <tbody>
          {filas.length === 0 && <tr><td colSpan={headers.length} style={{ padding: 12, color: '#94a3b8' }}>Sin registros en el período.</td></tr>}
          {filas.map((f, i) => (
            <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
              {f.map((c, ci) => <td key={ci} style={{ padding: '6px 10px', textAlign: headers[ci].right ? 'right' : 'left', fontWeight: headers[ci].right ? 700 : 400, color: headers[ci].right ? '#334155' : '#64748b' }}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Tabla titulo="📄 Anexo 1 · Ventas del período" color="#0284c7"
        headers={[{ label: 'Orden' }, { label: 'Fecha' }, { label: 'Cliente' }, { label: 'Total', right: true }]}
        filas={ax.ventas.map(v => [v.numeroOrden, v.fecha, v.clienteNombre, fmtCop(v.total)])} />
      <Tabla titulo="📦 Anexo 2 · Costo de ventas por categoría" color="#dc2626"
        headers={[{ label: 'Categoría' }, { label: 'Ingreso', right: true }, { label: 'Costo', right: true }]}
        filas={ax.costos.map(c => [c.categoria, fmtCop(c.ingreso), fmtCop(c.costo)])} />
      <Tabla titulo="🧾 Anexo 3 · Gastos del período" color="#b45309"
        headers={[{ label: 'Categoría' }, { label: 'Monto', right: true }]}
        filas={ax.gastos.map(g => [g.categoria, fmtCop(g.monto)])} />
      {ax.compras.length > 0 && (
        <Tabla titulo="🛒 Anexo 4 · Compras de mercancía (inventario)" color="#7c3aed"
          headers={[{ label: 'Fecha' }, { label: 'Proveedor' }, { label: 'Concepto' }, { label: 'Monto', right: true }]}
          filas={ax.compras.map(c => [c.fecha, c.proveedor, c.concepto, fmtCop(c.monto)])} />
      )}
      {/* ✅ ERI-CARTERA-001: anexos de cartera */}
      {ax.cxc && ax.cxc.length > 0 && (
        <Tabla titulo="💰 Anexo 5 · Cartera por cobrar (CxC)" color="#0284c7"
          headers={[{ label: 'Orden' }, { label: 'Fecha' }, { label: 'Cliente' }, { label: 'Saldo', right: true }]}
          filas={ax.cxc.map(c => [c.numeroOrden, c.fecha, c.clienteNombre, fmtCop(c.saldo)])} />
      )}
      {ax.cxp && ax.cxp.length > 0 && (
        <Tabla titulo="📋 Anexo 6 · Cartera por pagar (CxP)" color="#dc2626"
          headers={[{ label: 'Fecha' }, { label: 'Proveedor' }, { label: 'Concepto' }, { label: 'Saldo', right: true }]}
          filas={ax.cxp.map(c => [c.fecha, c.proveedor, c.concepto, fmtCop(c.saldo)])} />
      )}
    </div>
  );
};

const VistaResumen = ({ eri }) => (
  <div style={s.card}>
    <table style={s.tablaERI}>
      <tbody>
        <Fila label="INGRESOS POR SERVICIOS" tipo="seccion" />
        <Fila label="Servicios de mano de obra" valor={eri.ingresos.servicios} />

        <Fila label="INGRESOS POR PRODUCTOS" tipo="seccion" />
        <Fila label="Venta de productos" valor={eri.ingresos.productos} />

        <Fila label="TOTAL INGRESOS" valor={eri.ingresos.total} tipo="total" />

        <Fila label="(-) COSTO DE VENTAS" tipo="seccion" />
        <Fila label="Costo de insumos (servicios)" valor={-eri.costoVentas.servicios} />
        <Fila label="Costo de venta de productos" valor={-eri.costoVentas.productos} />

        <Fila label="UTILIDAD BRUTA" valor={eri.utilidadBruta.total} tipo="utilidad"
          margen={eri.utilidadBruta.margen} />

        <Fila label="(-) GASTOS OPERATIVOS" tipo="seccion" />
        {eri.gastos.personal > 0 &&         <Fila label="Personal (nómina)" valor={-eri.gastos.personal} />}
        {eri.gastos.operativos > 0 &&       <Fila label="Operativos" valor={-eri.gastos.operativos} />}
        {eri.gastos.fijos > 0 &&            <Fila label="Fijos (arriendo, servicios)" valor={-eri.gastos.fijos} />}
        {eri.gastos.administrativos > 0 &&  <Fila label="Administrativos" valor={-eri.gastos.administrativos} />}

        <Fila label="UTILIDAD OPERATIVA" valor={eri.utilidadOperativa.valor} tipo="utilidad"
          margen={eri.utilidadOperativa.margen} />

        {eri.gastos.financieros > 0 && (
          <>
            <Fila label="(-) GASTOS FINANCIEROS" tipo="seccion" />
            <Fila label="Comisiones bancarias / intereses" valor={-eri.gastos.financieros} />
            <Fila label="UTILIDAD ANTES DE IMPUESTOS" valor={eri.utilidadAntesImpuestos.valor} tipo="utilidad" />
          </>
        )}

        {eri.gastos.fiscales > 0 && (
          <>
            <Fila label="(-) IMPUESTOS" tipo="seccion" />
            <Fila label="Fiscales" valor={-eri.gastos.fiscales} />
          </>
        )}

        <Fila label="UTILIDAD NETA" valor={eri.utilidadNeta.valor} tipo="neta"
          margen={eri.utilidadNeta.margen} />
      </tbody>
    </table>

    {/* Desglose ingresos por empresa */}
    {eri.ingresos.porEmpresa && eri.ingresos.porEmpresa.length > 1 && (
      <div style={{ marginTop: 24 }}>
        <h4 style={{ fontSize: 14, color: '#374151', marginBottom: 12 }}>💼 Ingresos por empresa</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {eri.ingresos.porEmpresa.map(emp => (
            <div key={emp.empresaId} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>{emp.empresaNombre}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0284c7', marginTop: 4 }}>{fmtCop(emp.monto)}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                {emp.cantidadOrdenes} órdenes · {fmtPct(emp.porcentaje)} del total
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const Fila = ({ label, valor, margen, tipo }) => {
  const colores = {
    seccion: { bg: '#f3f4f6', color: '#374151', weight: 700, size: 12 },
    total:   { bg: '#eff6ff', color: '#1e3a8a', weight: 800, size: 14 },
    utilidad:{ bg: '#f0fdf4', color: '#15803d', weight: 800, size: 14 },
    neta:    { bg: '#7c3aed', color: '#fff',    weight: 900, size: 16 }
  };
  const c = colores[tipo] || { bg: '#fff', color: '#111', weight: 400, size: 13 };
  return (
    <tr style={{ background: c.bg }}>
      <td style={{ padding: '8px 14px', color: c.color, fontWeight: c.weight, fontSize: c.size, borderBottom: '1px solid #e5e7eb' }}>
        {label}
        {margen !== undefined && (
          <span style={{ marginLeft: 12, fontSize: 11, color: c.color, opacity: 0.85, fontWeight: 600 }}>
            ({fmtPct(margen)})
          </span>
        )}
      </td>
      <td style={{ padding: '8px 14px', textAlign: 'right', color: valor < 0 ? '#dc2626' : c.color, fontWeight: c.weight, fontSize: c.size, borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>
        {valor !== undefined ? fmtCop(Math.abs(valor)) : ''}
      </td>
    </tr>
  );
};

const VistaLineas = ({ eri }) => {
  const lineas = eri.porLinea || [];
  if (lineas.length === 0) {
    return <div style={{ ...s.card, textAlign: 'center', color: '#9ca3af', padding: 40 }}>
      Sin movimientos por línea en este período
    </div>;
  }
  // ✅ FIX ERI-LINEA-003: la vista por línea NO cubre todos los ingresos —
  // solo los servicios y los productos cuya categoría hace match con alguna
  // línea. Si alguien suma las tarjetas y lo compara con el total de ingresos,
  // le va a faltar plata y va a pensar que el informe está mal. Decirlo evita
  // esa confusión y además señala cuánto del negocio está sin clasificar.
  const totalIngresosERI = eri.ingresos?.total || 0;
  const sumaLineas = lineas.reduce((a, l) => a + (l.ingresoServicio || 0), 0);
  const cobertura = totalIngresosERI > 0 ? (sumaLineas / totalIngresosERI) * 100 : 0;
  const sinLinea = totalIngresosERI - sumaLineas;

  return (
    <div>
      {totalIngresosERI > 0 && (
        <div style={{
          background: cobertura >= 80 ? '#f0fdf4' : cobertura >= 50 ? '#fffbeb' : '#fef2f2',
          border: `1px solid ${cobertura >= 80 ? '#bbf7d0' : cobertura >= 50 ? '#fde68a' : '#fecaca'}`,
          borderRadius: 12, padding: '14px 18px', marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'
        }}>
          <div style={{ textAlign: 'center', minWidth: 78 }}>
            <div style={{
              fontSize: 26, fontWeight: 900, lineHeight: 1,
              color: cobertura >= 80 ? '#16a34a' : cobertura >= 50 ? '#d97706' : '#dc2626'
            }}>{cobertura.toFixed(0)}%</div>
            <div style={{ fontSize: 9.5, color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 3 }}>
              Cobertura
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 260, fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
            Estas líneas explican <strong>{fmtCop(sumaLineas)}</strong> de tus{' '}
            <strong>{fmtCop(totalIngresosERI)}</strong> en ingresos.
            {sinLinea > 1 && (
              <> Los otros <strong>{fmtCop(sinLinea)}</strong> son ventas de productos cuya categoría
              no está asociada a ninguna línea de servicio, por eso no aparecen acá.</>
            )}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>
              Si querés que un producto cuente dentro de una línea, asociá su categoría a esa línea
              en Configuración → Líneas de servicio.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
      {lineas.map(l => (
        <div key={l.id} style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ background: l.color, color: '#fff', padding: '14px 18px', fontWeight: 700 }}>
            🎯 {l.nombre}
          </div>
          <div style={{ padding: 18 }}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 700 }}>Ingreso</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#0284c7' }}>{fmtCop(l.ingresoServicio)}</div>
            </div>
            {/* ✅ FIX ERI-LINEA-002: el costo de una línea suma dos cosas y
                antes se mostraban juntas. Al comparar contra el desglose por
                categoría del estado de resultados las cifras no coincidían y
                parecía un error de cálculo, cuando en realidad son dos lentes
                distintos sobre el mismo dinero. Ahora se ve el desglose. */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 700 }}>Costo directo</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>− {fmtCop(l.costoServicio)}</div>

              {(l.costoInsumos > 0 || l.costoProductos > 0) && (
                <div style={{ marginTop: 7, background: '#f8fafc', borderRadius: 8, padding: '8px 11px' }}>
                  {l.costoInsumos > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: '#64748b', padding: '2px 0' }}>
                      <span>Insumos de taller</span><span>{fmtCop(l.costoInsumos)}</span>
                    </div>
                  )}
                  {l.costoProductos > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: '#64748b', padding: '2px 0' }}>
                      <span>Costo de productos vendidos</span><span>{fmtCop(l.costoProductos)}</span>
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 5, lineHeight: 1.5, borderTop: '1px dashed #e2e8f0', paddingTop: 5 }}>
                    El costo de productos también aparece en el estado de resultados
                    agrupado por su categoría, no por línea. Por eso las dos vistas
                    no muestran la misma cifra: son lentes distintos sobre el mismo dinero.
                  </div>
                </div>
              )}

              {l.costoServicio === 0 && l.ingresoServicio > 0 && (
                <div style={{
                  marginTop: 7, background: '#fffbeb', border: '1px solid #fde68a',
                  borderRadius: 8, padding: '8px 11px', fontSize: 11, color: '#92400e', lineHeight: 1.55
                }}>
                  ⚠️ Esta línea no tiene ningún costo asociado, por eso el margen sale
                  del 100%. Revisá que los productos tengan costo parametrizado y que
                  los insumos estén clasificados como costo de servicio.
                </div>
              )}
            </div>
            <div style={{ paddingTop: 12, borderTop: '2px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 700 }}>Utilidad bruta</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: l.utilidadBruta >= 0 ? '#16a34a' : '#dc2626' }}>
                {fmtCop(l.utilidadBruta)}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Margen: <strong style={{ color: l.margenPct >= 30 ? '#16a34a' : l.margenPct >= 0 ? '#f59e0b' : '#dc2626' }}>
                  {fmtPct(l.margenPct)}
                </strong>
              </div>
            </div>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
};

const VistaDetalle = ({ eri }) => (
  <div>
    {/* Órdenes */}
    <div style={s.card}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>
        📦 Órdenes ({eri.detalleOrdenes?.length || 0})
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.tablaDetalle}>
          <thead>
            <tr>
              <th style={s.thd}>Orden</th>
              <th style={s.thd}>Fecha</th>
              <th style={s.thd}>Cliente</th>
              <th style={s.thd}>Empresa</th>
              <th style={s.thd}>Pago</th>
              <th style={{ ...s.thd, textAlign: 'right' }}>Servicios</th>
              <th style={{ ...s.thd, textAlign: 'right' }}>Productos</th>
              <th style={{ ...s.thd, textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(eri.detalleOrdenes || []).map(o => (
              <tr key={o.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={s.tdd}><code style={{ fontSize: 11 }}>{o.numeroOrden}</code></td>
                <td style={s.tdd}>{fmtFecha(o.fecha)}</td>
                <td style={s.tdd}>{o.clienteNombre}</td>
                <td style={s.tdd}>{o.empresaNombre || '—'}</td>
                <td style={s.tdd}>{o.formaPago || '—'}</td>
                <td style={{ ...s.tdd, textAlign: 'right' }}>{fmtCop(o.ingresoServicios)}</td>
                <td style={{ ...s.tdd, textAlign: 'right' }}>{fmtCop(o.ingresoProductos)}</td>
                <td style={{ ...s.tdd, textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{fmtCop(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Egresos */}
    <div style={{ ...s.card, marginTop: 20 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>
        💸 Egresos ({eri.detalleEgresos?.length || 0})
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.tablaDetalle}>
          <thead>
            <tr>
              <th style={s.thd}>Numero</th>
              <th style={s.thd}>Fecha</th>
              <th style={s.thd}>Concepto</th>
              <th style={s.thd}>Categoría</th>
              <th style={s.thd}>Tipo ERI</th>
              <th style={{ ...s.thd, textAlign: 'right' }}>Monto</th>
            </tr>
          </thead>
          <tbody>
            {(eri.detalleEgresos || []).map(e => (
              <tr key={e.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={s.tdd}><code style={{ fontSize: 11 }}>{e.numero}</code></td>
                <td style={s.tdd}>{fmtFecha(e.fecha)}</td>
                <td style={s.tdd}>{e.concepto}</td>
                <td style={s.tdd}>{e.categoria}</td>
                <td style={s.tdd}>{labelTipoERI(e.tipoERI)}</td>
                <td style={{ ...s.tdd, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmtCop(e.monto)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const labelTipoERI = (t) => ({
  'costo_servicio':       '💰 Costo servicio',
  'gasto_personal':       '👥 Personal',
  'gasto_operativo':      '⚙️ Operativo',
  'gasto_fijo':           '🏠 Fijo',
  'gasto_administrativo': '📋 Administrativo',
  'gasto_financiero':     '🏦 Financiero',
  'gasto_fiscal':         '📑 Fiscal',
}[t] || t);

// ─── HTML para imprimir ──────────────────────────────────────────────────────
const construirHtmlImprimir = (eri) => {
  const inf = eri.informe || {};
  const m = eri.meta || {};
  const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
  const hoy = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });

  // Filas de una sección de categorías
  const filasCat = (arr) => (arr || []).map(c =>
    `<tr><td class="ind">${c.nombre}</td><td class="num">${fmt(c.valor)}</td></tr>`).join('');

  // Anexo genérico ordenado
  const tablaAnexo = (titulo, headers, filas, colorHead) => `
    <div class="anexo">
      <div class="anexo-titulo" style="border-left:4px solid ${colorHead}">${titulo}</div>
      <table class="tabla-anexo">
        <thead><tr>${headers.map(h => `<th class="${h.num ? 'num' : ''}">${h.l}</th>`).join('')}</tr></thead>
        <tbody>${filas || `<tr><td colspan="${headers.length}" class="vacio">Sin registros</td></tr>`}</tbody>
      </table>
    </div>`;

  return `
<html>
<head>
  <meta charset="utf-8">
  <title>Estado de Resultados — ${m.empresaNombre || 'Control360'} — ${m.desde} a ${m.hasta}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; color: #1e293b; margin: 0; padding: 0; font-size: 12.5px; }
    .hoja { padding: 40px 46px; }
    /* MEMBRETE */
    .membrete { border-bottom: 3px solid #4f46e5; padding-bottom: 18px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: flex-end; }
    .brand { display: flex; flex-direction: column; }
    .brand-logo { font-size: 26px; font-weight: 800; letter-spacing: -0.5px; }
    .brand-logo .c360 { color: #4f46e5; }
    .brand-logo .n { color: #0f172a; }
    .brand-tag { font-size: 9px; letter-spacing: 3px; color: #94a3b8; text-transform: uppercase; margin-top: 2px; font-weight: 600; }
    .empresa-info { text-align: right; font-size: 11px; color: #475569; line-height: 1.5; }
    .empresa-info .nom { font-size: 14px; font-weight: 800; color: #0f172a; }
    /* TÍTULO INFORME */
    .doc-titulo { text-align: center; margin: 22px 0 6px; }
    .doc-titulo h1 { font-size: 20px; color: #0f172a; margin: 0; letter-spacing: 0.5px; }
    .doc-titulo .periodo { font-size: 12px; color: #64748b; margin-top: 4px; }
    .doc-titulo .norma { font-size: 10px; color: #94a3b8; font-style: italic; margin-top: 2px; }
    /* TABLA PRINCIPAL */
    table { width: 100%; border-collapse: collapse; }
    .tabla-eri td { padding: 7px 14px; }
    .tabla-eri .ind { padding-left: 30px; color: #475569; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .seccion-h td { font-size: 11px; font-weight: 800; letter-spacing: 0.5px; text-transform: uppercase; padding-top: 16px; }
    .subtotal td { border-top: 1px solid #cbd5e1; font-weight: 700; }
    .destacado td { font-weight: 800; font-size: 14px; padding: 11px 14px; border-radius: 6px; }
    .util-bruta td { background: #f0fdf4; color: #16a34a; }
    .util-op td { background: #eff6ff; color: #2563eb; }
    .util-neta td { background: linear-gradient(90deg,#4f46e5,#7c3aed); color: #fff; font-size: 16px; }
    /* INVENTARIO / CARTERA */
    .info-box { margin-top: 20px; border: 1px dashed #cbd5e1; border-radius: 10px; padding: 14px 18px; background: #f8fafc; }
    .info-box .t { font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
    .info-grid { display: flex; gap: 20px; flex-wrap: wrap; }
    .info-item { flex: 1; min-width: 130px; }
    .info-item .lbl { font-size: 10px; color: #94a3b8; }
    .info-item .val { font-size: 15px; font-weight: 800; }
    /* ANEXOS */
    .anexo { page-break-inside: auto; margin-top: 22px; }
    .anexo-titulo { font-size: 13px; font-weight: 800; color: #334155; padding: 6px 0 6px 12px; margin-bottom: 8px; }
    .tabla-anexo { font-size: 11px; }
    .tabla-anexo th { text-align: left; padding: 6px 10px; background: #f1f5f9; color: #475569; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 2px solid #e2e8f0; }
    .tabla-anexo td { padding: 5px 10px; border-bottom: 1px solid #f1f5f9; }
    .tabla-anexo tr { page-break-inside: avoid; }
    .tabla-anexo thead { display: table-header-group; }
    .vacio { color: #94a3b8; text-align: center; padding: 12px; }
    .salto { page-break-before: always; }
    .footer { margin-top: 30px; padding-top: 12px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 10px; text-align: center; }
    @media print { .hoja { padding: 20px 26px; } }
  </style>
</head>
<body>
  <!-- ══════ HOJA 1: ESTADO DE RESULTADOS ══════ -->
  <div class="hoja">
    <div class="membrete">
      <div class="brand">
        <div class="brand-logo"><span class="c360">Control</span><span class="n">360</span></div>
        <div class="brand-tag">Sistema Operativo Empresarial</div>
      </div>
      <div class="empresa-info">
        <div class="nom">${m.empresaNombre || 'Consolidado'}</div>
        ${m.nit ? `<div>NIT: ${m.nit}</div>` : ''}
        <div>Informe generado: ${hoy}</div>
      </div>
    </div>

    <div class="doc-titulo">
      <h1>ESTADO DE RESULTADOS INTEGRAL</h1>
      <div class="periodo">Período: ${m.desde} al ${m.hasta}</div>
      <div class="norma">Elaborado bajo el principio de causación (devengo) — Norma contable colombiana</div>
    </div>

    <table class="tabla-eri">
      <tr class="seccion-h"><td style="color:#0284c7">INGRESOS OPERACIONALES</td><td></td></tr>
      ${filasCat(inf.ingresos?.porCategoria)}
      <tr class="subtotal"><td>Total ingresos operacionales</td><td class="num">${fmt(inf.ingresos?.total)}</td></tr>

      ${(inf.costoVentas?.porCategoria || []).length ? `
      <tr class="seccion-h"><td style="color:#dc2626">(−) COSTO DE VENTAS</td><td></td></tr>
      ${filasCat(inf.costoVentas.porCategoria)}
      <tr class="subtotal"><td>Total costo de ventas</td><td class="num">${fmt(inf.costoVentas.total)}</td></tr>
      ` : ''}

      <tr class="destacado util-bruta"><td>= UTILIDAD BRUTA</td><td class="num">${fmt(inf.utilidadBruta)}</td></tr>

      ${(inf.gastos?.porCategoria || []).length ? `
      <tr class="seccion-h"><td style="color:#b45309">(−) GASTOS OPERACIONALES</td><td></td></tr>
      ${filasCat(inf.gastos.porCategoria)}
      <tr class="subtotal"><td>Total gastos operacionales</td><td class="num">${fmt(inf.gastos.total)}</td></tr>
      ` : ''}

      <tr class="destacado util-neta"><td>= UTILIDAD NETA DEL PERÍODO</td><td class="num">${fmt(inf.utilidadNeta)}</td></tr>
    </table>

    ${inf.inventario ? `
    <div class="info-box">
      <div class="t">📦 Inventario (informativo — no afecta el resultado)</div>
      <div class="info-grid">
        <div class="info-item"><div class="lbl">Compras del período</div><div class="val" style="color:#7c3aed">${fmt(inf.inventario.comprasDelPeriodo)}</div></div>
        <div class="info-item"><div class="lbl">Stock al costo</div><div class="val" style="color:#334155">${fmt(inf.inventario.alCosto)}</div></div>
        <div class="info-item"><div class="lbl">Stock valorizado</div><div class="val" style="color:#16a34a">${fmt(inf.inventario.valorizado)}</div></div>
        <div class="info-item"><div class="lbl">Utilidad potencial</div><div class="val" style="color:#059669">${fmt(inf.inventario.potencial || 0)}</div></div>
      </div>
    </div>` : ''}

    ${inf.cartera ? `
    <div class="info-box">
      <div class="t">💰 Cartera (informativo — no afecta el resultado)</div>
      <div class="info-grid">
        <div class="info-item"><div class="lbl">Por cobrar (CxC) · ${inf.cartera.cxc.detalle.length} órdenes</div><div class="val" style="color:#0284c7">${fmt(inf.cartera.cxc.total)}</div></div>
        <div class="info-item"><div class="lbl">Por pagar (CxP) · ${inf.cartera.cxp.detalle.length} cuentas</div><div class="val" style="color:#dc2626">${fmt(inf.cartera.cxp.total)}</div></div>
      </div>
    </div>` : ''}
  </div>

  <!-- ══════ HOJAS SIGUIENTES: ANEXOS ══════ -->
  <div class="hoja salto">
    <div class="membrete">
      <div class="brand"><div class="brand-logo"><span class="c360">Control</span><span class="n">360</span></div><div class="brand-tag">Anexos de soporte</div></div>
      <div class="empresa-info"><div class="nom">${m.empresaNombre || 'Consolidado'}</div><div>${m.desde} al ${m.hasta}</div></div>
    </div>

    ${tablaAnexo('Anexo 1 · Ventas del período (ordenado por N° de orden)',
      [{l:'Orden'},{l:'Fecha'},{l:'Cliente'},{l:'Estado'},{l:'Total',num:true}],
      (inf.anexos?.ventas || []).map(v => `<tr><td><strong>${v.numeroOrden}</strong></td><td>${v.fecha}</td><td>${v.clienteNombre}</td><td>${v.estado || ''}</td><td class="num">${fmt(v.total)}</td></tr>`).join(''),
      '#0284c7')}

    ${tablaAnexo('Anexo 2 · Costo de ventas por categoría',
      [{l:'Categoría'},{l:'Ingreso',num:true},{l:'Costo',num:true}],
      (inf.anexos?.costos || []).map(c => `<tr><td>${c.categoria}</td><td class="num">${fmt(c.ingreso)}</td><td class="num">${fmt(c.costo)}</td></tr>`).join(''),
      '#dc2626')}

    ${tablaAnexo('Anexo 3 · Egresos del período (ordenado por N° de comprobante)',
      [{l:'N° Egreso'},{l:'Fecha'},{l:'Categoría'},{l:'Concepto'},{l:'Monto',num:true}],
      (inf.anexos?.egresos || []).map(e => `<tr><td><strong>${e.numero}</strong></td><td>${e.fecha}</td><td>${e.categoria}</td><td>${e.concepto}</td><td class="num">${fmt(e.monto)}</td></tr>`).join(''),
      '#b45309')}

    ${(inf.anexos?.compras || []).length ? tablaAnexo('Anexo 4 · Compras de mercancía (inventario)',
      [{l:'Fecha'},{l:'Proveedor'},{l:'Concepto'},{l:'Monto',num:true}],
      inf.anexos.compras.map(c => `<tr><td>${c.fecha}</td><td>${c.proveedor}</td><td>${c.concepto}</td><td class="num">${fmt(c.monto)}</td></tr>`).join(''),
      '#7c3aed') : ''}

    ${(inf.anexos?.cxc || []).length ? tablaAnexo('Anexo 5 · Cartera por cobrar (CxC)',
      [{l:'Orden'},{l:'Fecha'},{l:'Cliente'},{l:'Saldo',num:true}],
      inf.anexos.cxc.map(c => `<tr><td><strong>${c.numeroOrden}</strong></td><td>${c.fecha}</td><td>${c.clienteNombre}</td><td class="num">${fmt(c.saldo)}</td></tr>`).join(''),
      '#0284c7') : ''}

    ${(inf.anexos?.cxp || []).length ? tablaAnexo('Anexo 6 · Cartera por pagar (CxP)',
      [{l:'Fecha'},{l:'Proveedor'},{l:'Concepto'},{l:'Saldo',num:true}],
      inf.anexos.cxp.map(c => `<tr><td>${c.fecha}</td><td>${c.proveedor}</td><td>${c.concepto}</td><td class="num">${fmt(c.saldo)}</td></tr>`).join(''),
      '#dc2626') : ''}

    <div class="footer">Control360 · Sistema Operativo Empresarial — Informe generado el ${new Date().toLocaleString('es-CO')}</div>
  </div>
</body>
</html>`;
};


const s = {
  wrapper:    { padding: '24px 32px', maxWidth: 1400, margin: '0 auto', fontFamily: "'Segoe UI', sans-serif" },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  pageTitle:  { margin: 0, fontSize: 26, fontWeight: 700, color: '#111' },
  pageSubtitle:{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 },
  btn:        { padding: '9px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 },

  filtros: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 14 },
  presets: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  preset:  { padding: '6px 14px', borderRadius: 20, border: '1px solid #e5e7eb', cursor: 'pointer', background: '#fff', color: '#6b7280', fontSize: 12, fontWeight: 600 },
  presetActivo: { background: '#7c3aed', color: '#fff', border: '1px solid #7c3aed' },
  campoF:  { display: 'flex', flexDirection: 'column', gap: 4 },
  labelF:  { fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' },
  inputF:  { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 },

  kpisGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 20 },
  kpi:      { borderRadius: 12, padding: 18 },

  tabs:     { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab:      { padding: '10px 18px', borderRadius: 10, border: '1px solid #e5e7eb', cursor: 'pointer', background: '#fff', color: '#6b7280', fontSize: 13, fontWeight: 600 },
  tabActivo:{ background: '#1a1a2e', color: '#fff', border: '1px solid #1a1a2e' },

  card:     { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 },
  tablaERI: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },

  tablaDetalle: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  thd:      { padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb', background: '#f9fafb' },
  tdd:      { padding: '7px 10px', color: '#374151' },

  cargando: { textAlign: 'center', padding: 60, color: '#7c3aed', fontSize: 14 },
  error:    { background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', padding: 14, borderRadius: 10, marginBottom: 16, fontSize: 13 }
};

export default ModuloERI;

